import { ChevronDown, ChevronUp, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { EditableCell, EditableText } from "@/components/admin/EditableCell";
import { fmtBT, fmtRate } from "@/lib/format";

const fmtMoney = (v) => new Intl.NumberFormat("nb-NO", { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);

const MoveBtn = ({ onClick, disabled, label, testId, Icon }) => (
  <button type="button" onClick={onClick} disabled={disabled} aria-label={label} title={label} data-testid={testId}
    className="p-1 rounded text-slate-400 hover:text-sky-700 hover:bg-sky-50 disabled:opacity-25 disabled:pointer-events-none transition-colors">
    <Icon className="w-4 h-4" />
  </button>
);

const TierRow = ({ tier, i, tid, onTier, onRemove, canRemove, onMove, count }) => (
  <tr className="border-t border-slate-100">
    <td className="py-1 pr-1 w-8">
      <div className="flex flex-col">
        <MoveBtn onClick={() => onMove(i, -1)} disabled={i === 0} label="Flytt opp" testId={`tier-${tid}-${i}-up`} Icon={ChevronUp} />
        <MoveBtn onClick={() => onMove(i, 1)} disabled={i === count - 1} label="Flytt ned" testId={`tier-${tid}-${i}-down`} Icon={ChevronDown} />
      </div>
    </td>
    <td className="py-1 pr-2"><EditableCell value={tier.from_bt} format={fmtBT} onCommit={(v) => onTier(i, { from_bt: v })} testId={`tier-${tid}-${i}-from`} /></td>
    <td className="py-1 pr-2"><EditableCell value={tier.to_bt} format={fmtBT} allowEmpty emptyLabel="og over" onCommit={(v) => onTier(i, { to_bt: v })} testId={`tier-${tid}-${i}-to`} /></td>
    <td className="py-1 pr-2"><EditableCell value={tier.rate} format={fmtRate} onCommit={(v) => onTier(i, { rate: v })} testId={`tier-${tid}-${i}-rate`} className="font-medium text-slate-900" /></td>
    <td className="py-1 w-9">
      {canRemove && (
        <button type="button" onClick={() => onRemove(i)} className="p-2 rounded-md text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors" aria-label="Slett rad" data-testid={`tier-${tid}-${i}-delete`}>
          <Trash2 className="w-4 h-4" />
        </button>
      )}
    </td>
  </tr>
);

const DeleteTable = ({ name, onDelete, tid }) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant="ghost" size="sm" className="text-slate-500 hover:text-red-600" data-testid={`table-${tid}-delete`}>
        <Trash2 className="w-4 h-4" />
      </Button>
    </AlertDialogTrigger>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Slette «{name}»?</AlertDialogTitle>
        <AlertDialogDescription>Tabellen fjernes fra kalkulatoren med en gang.</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Avbryt</AlertDialogCancel>
        <AlertDialogAction onClick={onDelete} className="bg-red-600 hover:bg-red-700" data-testid={`table-${tid}-delete-confirm`}>Slett</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const FeeTableCard = ({ table, onChange, onDelete, canDelete, unit = "BT", badge }) => {
  const tid = table.id;
  const cols = table.cols || { from: `Fra ${unit}`, to: `Til ${unit}`, rate: `Sats kr/${unit}` };
  const set = (patch) => onChange({ ...table, ...patch });
  const onTier = (i, patch) => set({ tiers: table.tiers.map((t, j) => (j === i ? { ...t, ...patch } : t)) });
  const onRemove = (i) => {
    const tiers = table.tiers.filter((_, j) => j !== i);
    if (i > 0 && i < table.tiers.length - 1) tiers[i] = { ...tiers[i], from_bt: tiers[i - 1].to_bt ?? tiers[i].from_bt };
    if (i === table.tiers.length - 1 && table.tiers[i].to_bt == null) tiers[tiers.length - 1] = { ...tiers[tiers.length - 1], to_bt: null };
    set({ tiers });
  };
  const onMove = (i, dir) => {
    const tiers = [...table.tiers];
    [tiers[i], tiers[i + dir]] = [tiers[i + dir], tiers[i]];
    set({ tiers });
  };
  const addTier = () => {
    const tiers = [...table.tiers];
    const last = tiers[tiers.length - 1];
    if (!last) return set({ tiers: [{ from_bt: 0, to_bt: null, rate: 0 }] });
    if (last.to_bt == null) tiers[tiers.length - 1] = { ...last, to_bt: last.from_bt + 1000 };
    const from = tiers[tiers.length - 1].to_bt;
    set({ tiers: [...tiers, { from_bt: from, to_bt: null, rate: 0 }] });
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg fade-up" data-testid={`fee-table-${tid}`}>
      <div className="flex items-center justify-between gap-2 px-4 sm:px-5 pt-4">
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          <EditableText value={table.name} onCommit={(v) => set({ name: v })} testId={`table-${tid}-name`} className="font-display text-lg font-semibold text-slate-900" />
          {badge && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-50 text-sky-800 border border-sky-100" data-testid={`table-${tid}-badge`}>{badge}</span>}
        </div>
        {canDelete && <DeleteTable name={table.name} onDelete={onDelete} tid={tid} />}
      </div>
      <div className="grid sm:grid-cols-3 gap-3 px-4 sm:px-5 py-4">
        <div className="rounded-md bg-sky-50/70 border border-sky-100 px-3 py-2">
          <div className="text-xs font-medium text-sky-900 mb-0.5">Minstepris</div>
          <EditableCell value={table.min_price} format={fmtMoney} onCommit={(v) => set({ min_price: v })} testId={`table-${tid}-min-price`} className="font-semibold text-slate-900 text-base" />
        </div>
        <div className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2">
          <div className="text-xs font-medium text-slate-600 mb-1">Beregningsmetode</div>
          <Select value={table.method} onValueChange={(v) => set({ method: v })}>
            <SelectTrigger className="h-9 bg-white" data-testid={`table-${tid}-method`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="progressive">Trinnvis per intervall</SelectItem>
              <SelectItem value="flat">{unit === "BT" ? "Hele BT etter intervall" : "Hele lengden etter intervall"}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <label className="rounded-md bg-slate-50 border border-slate-100 px-3 py-2 flex items-center justify-between gap-2 cursor-pointer">
          <span className="text-xs font-medium text-slate-600 leading-tight">Ganges med<br />antall døgn</span>
          <Switch checked={!!table.per_day} onCheckedChange={(v) => set({ per_day: v })} data-testid={`table-${tid}-per-day`} />
        </label>
      </div>
      <div className="px-4 sm:px-5 pb-4 overflow-x-auto">
        <table className="w-full text-sm min-w-[320px]">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th />
              <th className="pb-2 font-medium px-2" data-testid={`table-${tid}-col-from`}>{cols.from}</th>
              <th className="pb-2 font-medium px-2" data-testid={`table-${tid}-col-to`}>{cols.to}</th>
              <th className="pb-2 font-medium px-2" data-testid={`table-${tid}-col-rate`}>{cols.rate}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {table.tiers.map((t, i) => (
              <TierRow key={i} tier={t} i={i} tid={tid} onTier={onTier} onRemove={onRemove} canRemove={table.tiers.length > 1} onMove={onMove} count={table.tiers.length} />
            ))}
          </tbody>
        </table>
        <Button variant="ghost" size="sm" onClick={addTier} className="mt-2 text-sky-700 hover:text-sky-800 hover:bg-sky-50" data-testid={`table-${tid}-add-tier`}>
          <Plus className="w-4 h-4 mr-1" /> Legg til intervall
        </Button>
      </div>
    </div>
  );
};
